/**
 * Operations Page - Frontend
 * @description Simple operations page inspired by POC - shows operations for a specific device
 * Similar to POC OperationsPage.tsx but using real backend data
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { FileSpreadsheet, RefreshCw, Download, Map } from 'lucide-react';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useMapOperations } from '@/hooks/useMapOperations';

// Components
import { DeviceOperationsTable } from '@/components/features/operations';
import { LoadingOverlay } from '@/components/ui/LoadingOverlay';

// Services
import { deviceService, dashboardService } from '@/services/api';
import { formatDateAsYearMonthDayHourMinute } from '@/utils/formatters/dateFormatter';

// Hooks and contexts
import { useToast } from '@/contexts/ToastContext';
import { useNavigationHelpers } from '@/utils/navigationHelpers';
import { useExcelExport } from '@/hooks/useExcelExport';
import { useAuth } from '@/contexts/AuthContext';

// Types
import { Device } from '@/types/device.types';
// import { OperationResponse } from '@/types/operation.types';
import { DashboardOperation } from '@/types/dashboard.types';

const sortOperationsByStartDateDesc = (operations: DashboardOperation[]): DashboardOperation[] =>
  [...operations].sort((a, b) => {
    const timeA = a.startTimestamp ? new Date(a.startTimestamp).getTime() : 0;
    const timeB = b.startTimestamp ? new Date(b.startTimestamp).getTime() : 0;
    return timeB - timeA;
  });
// import { TableButton } from '@/components/ui/TableButton';
// import { PERMISSIONS } from '@/contexts/PermissionsContext';
// import { OperationResponse, OperationsTableData } from '@/types';

export const DeviceOperationsPage: React.FC = () => {
  // Hooks from react-router-dom and i18n
  const navigate = useNavigate();
  const { deviceId } = useParams<{ deviceId: string }>();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { navigateWithTokenCheck } = useNavigationHelpers();
  const { user } = useAuth();
  const { exportOperations, generateFileName, isExporting } = useExcelExport({ showToasts: false });

  // State management
  const [device, setDevice] = useState<Device | null>(null);
  const [operations, setOperations] = useState<DashboardOperation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [isCheckingSecurity, setIsCheckingSecurity] = useState(true);

  // Security validation function
  const validateDeviceAccess = useCallback(async () => {
    if (!user?.id || !deviceId) {
      showToast(t('errors.userNotAuthenticated'), 'error');
      await navigateWithTokenCheck(navigate, '/operations');
      return;
    }

    try {
      setIsCheckingSecurity(true);

      // For Administrators, they can access any device
      if (user.role === 'Administrador') {
        setIsCheckingSecurity(false);
        return;
      }

      // For Supervisors and Operarios, check if they have access to this specific device
      if (user.role === 'Supervisor' || user.role === 'Operario') {
        const userDevicesResponse = await deviceService.getByUserId(user.id);

        if (!userDevicesResponse.success || !userDevicesResponse.data) {
          showToast(t('errors.failedToCheckDeviceAccess'), 'error');
          await navigateWithTokenCheck(navigate, '/operations');
          return;
        }

        // Check if the device ID is in the user's associated devices
        const hasAccess = userDevicesResponse.data.some((device) => device.id === deviceId);

        if (!hasAccess) {
          showToast(t('errors.youDontHaveAccessToThisDevice'), 'error');
          await navigateWithTokenCheck(navigate, '/operations');
          return;
        }
      }

      setIsCheckingSecurity(false);
    } catch (error) {
      console.error('Error validating device access:', error);
      showToast(t('errors.securityValidationFailed'), 'error');
      await navigateWithTokenCheck(navigate, '/operations');
    }
  }, [user, deviceId, t, showToast, navigate, navigateWithTokenCheck]);

  // Refs
  const { mapContainer, flyToOperation, refreshExtent } = useMapOperations({
    operations,
    onMarkerClick: setSelectedRowId,
  });

  // Handler functions for the table
  const handleCenterMap = (operationId: string) => {
    setSelectedRowId(operationId);
    flyToOperation(operationId);
  };

  const handleViewLectures = async (deviceId: string, operationId: string) => {
    await navigateWithTokenCheck(
      navigate,
      `/operations/device/${deviceId}/operation/${operationId}/lectures`
    );
  };

  const handleRowClick = (operationId: string) => {
    setSelectedRowId(operationId);
    // flyToOperation(operationId);
  };

  // Security validation effect
  useEffect(() => {
    if (user?.id && deviceId) {
      validateDeviceAccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, deviceId]); // Remove validateDeviceAccess from dependencies to prevent loop

  // Load data effect - separate from security validation
  useEffect(() => {
    if (!deviceId || isCheckingSecurity) return;

    const loadData = async () => {
      try {
        setLoading(true);

        // Load device info and operations in parallel
        const [deviceResponse, dashboardOperationByDeviceResponse] = await Promise.all([
          deviceService.getById(deviceId),
          dashboardService.getOperationsByDeviceId(deviceId, {
            limit: 500,
            sortBy: 'startTimestamp',
            sortOrder: 'desc',
          }),
        ]);

        // Handle device response
        if (!deviceResponse.success) {
          throw new Error(deviceResponse.error.message || 'Device not found');
        }
        if (!deviceResponse.data) {
          throw new Error('Device not found');
        }
        setDevice(deviceResponse.data);

        // Handle operations response
        if (!dashboardOperationByDeviceResponse.success) {
          console.warn('No operations found for device:', deviceId);
          setOperations([]);
          return;
        }

        // Convert dashboard operations to OperationResponse format
        const apiDashboardOperations: DashboardOperation[] =
          dashboardOperationByDeviceResponse.data.map((operation: DashboardOperation) => ({
            ...operation,
            centroid_latitutde: operation.centroidLocation?.latitude,
            centroid_longitude: operation.centroidLocation?.longitude,
          }));

        setOperations(sortOperationsByStartDateDesc(apiDashboardOperations));
      } catch (error) {
        console.error('Error loading operations:', error);
        showToast(t('pages.operations.loadError'), 'error');

        // If device not found, redirect back
        if (error instanceof Error && error.message.includes('not found')) {
          await navigateWithTokenCheck(navigate, '/operations');
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, isCheckingSecurity]); // Only reload when deviceId changes or security check completes

  const handleRefresh = async () => {
    if (!deviceId) return;

    try {
      setLoading(true);

      // Load device info and operations in parallel
      const [deviceResponse, dashboardOperationByDeviceResponse] = await Promise.all([
        deviceService.getById(deviceId),
        dashboardService.getOperationsByDeviceId(deviceId, {
          limit: 500,
          sortBy: 'startTimestamp',
          sortOrder: 'desc',
        }),
      ]);

      // Handle device response
      if (!deviceResponse.success) {
        throw new Error(deviceResponse.error.message || 'Device not found');
      }
      if (!deviceResponse.data) {
        throw new Error('Device not found');
      }
      setDevice(deviceResponse.data);

      // Handle operations response
      if (!dashboardOperationByDeviceResponse.success) {
        console.warn('No operations found for device:', deviceId);
        setOperations([]);
        return;
      }

      // Convert dashboard operations to OperationResponse format
      const apiDashboardOperations: DashboardOperation[] =
        dashboardOperationByDeviceResponse.data.map((operation: DashboardOperation) => ({
          ...operation,
          centroid_latitutde: operation.centroidLocation?.latitude,
          centroid_longitude: operation.centroidLocation?.longitude,
        }));

      setOperations(sortOperationsByStartDateDesc(apiDashboardOperations));
    } catch (error) {
      console.error('Error loading operations:', error);
      showToast(t('pages.operations.loadError'), 'error');
    } finally {
      setLoading(false);
    }
  };
  const handleRefreshExtent = () => {
    refreshExtent();
  };

  const deviceName = device?.vehicle?.plate || device?.serialNumber || deviceId;

  // Función para exportar a CSV
  const handleExportCSV = useCallback(() => {
    if (operations.length === 0) {
      showToast(t('common.noDataToExport'), 'warning');
      return;
    }

    // Definir las columnas que queremos exportar (traducidas)
    const headers = [
      t('pages.operations.columns.operationId'),
      t('pages.operations.columns.operationStartTime'),
      t('pages.operations.columns.operationEndTime'),
      t('pages.operations.columns.operationDuration'),
      t('pages.operations.columns.lectureCountExported'),
      t('pages.operations.columns.centroid_latitutde'),
      t('pages.operations.columns.centroid_longitude'),
    ];

    // Función para escapar valores CSV
    const escapeCSV = (value: string | number) => {
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const formatOpDate = (ts: string | undefined, tz?: string) =>
      ts ? formatDateAsYearMonthDayHourMinute(ts, tz) : 'N/A';

    // Crear el contenido CSV
    const csvRows = [
      headers.join(','),
      ...operations.map((operation) =>
        [
          operation.operationIdentifier || '',
          formatOpDate(operation.startTimestamp, operation.tz),
          formatOpDate(operation.endTimestamp, operation.tz),
          operation.durationMinutes || 0,
          operation.lectureCount || 0,
          operation.centroid_latitutde || '',
          operation.centroid_longitude || '',
        ]
          .map(escapeCSV)
          .join(',')
      ),
    ];

    const csvContent = csvRows.join('\n');

    // Crear y descargar el archivo
    const blob = new globalThis.Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });
    const link = document.createElement('a');
    const url = globalThis.URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `operaciones_${deviceName}_${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    globalThis.URL.revokeObjectURL(url);

    showToast(t('common.exportSuccessful'), 'success');
  }, [operations, deviceName, showToast, t]);

  // Función para exportar a XLSX
  const handleExportXLSX = useCallback(async () => {
    try {
      const fileName = generateFileName('operaciones', deviceName);
      await exportOperations(operations, fileName, deviceName);
      showToast(t('common.exportSuccessful'), 'success');
    } catch (error) {
      showToast(
        `❌ Error al exportar: ${error instanceof Error ? error.message : 'Error desconocido'}`,
        'error'
      );
    }
  }, [operations, deviceName, exportOperations, generateFileName, showToast, t]);

  return (
    <div className="bg-brand-white">
      {/* Security Check Overlay */}
      {isCheckingSecurity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full mx-4">
            <div className="flex items-center justify-center mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
            <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
              {t('common.loading')}
            </h3>
            <p className="text-sm text-gray-600 text-center">
              {t('pages.operations.validatingAccess')}
            </p>
          </div>
        </div>
      )}

      {/* Loading Overlay - Reusable Component */}
      <LoadingOverlay
        loading={loading}
        title={t('common.loading')}
        message={t('pages.operations.loadingOperations')}
      />

      {/* Header */}
      <div className="flex items-center justify-between p-2 bg-white border-b border-gray-200">
        <div className="flex items-center space-x-4">
          {/*
          <button
            onClick={handleBackToDevices}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.back')}
          </button>
          */}

          <div>
            <h2 className="text-1xl font-bold text-gray-900">
              {t('pages.operations.title', { serialNumber: deviceName })}
            </h2>
            <p className="text-gray-600 mt-1">
              {t('pages.operations.subtitle', { count: operations.length })}
            </p>
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleRefreshExtent}
            disabled={operations.length === 0}
            className="bg-brand-orange text-brand-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Map className="h-4 w-4 mr-2" />
            {t('common.refreshExtent')}
          </button>
          {handleRefresh && (
            <button
              onClick={handleRefresh}
              className="bg-brand-orange text-brand-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors duration-200 flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('common.refresh')}
            </button>
          )}
          <button
            onClick={handleExportCSV}
            disabled={operations.length === 0}
            className="bg-brand-orange text-brand-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="h-4 w-4 mr-2" />
            {t('common.exportCSV')}
          </button>

          <button
            onClick={handleExportXLSX}
            disabled={operations.length === 0 || isExporting}
            className="bg-brand-orange text-brand-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExporting ? (
              <>
                <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Exportando...
              </>
            ) : (
              <>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                {t('common.exportXLSX')}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Grid Layout */}
      <div className="grid grid-cols-12 h-[calc(90vh-5rem)]">
        {/* Operations Table */}
        <div className="col-span-8 bg-white border-r border-gray-200 p-4">
          <DeviceOperationsTable
            operations={operations}
            loading={false}
            deviceId={deviceId}
            onCenterMap={handleCenterMap}
            onViewLectures={handleViewLectures}
            selectedRowId={selectedRowId}
            onRowClick={handleRowClick}
          />
        </div>

        {/* Map */}
        <div ref={mapContainer} className="col-span-4" />
      </div>
    </div>
  );
};
